import { XCircleIcon } from '@heroicons/react/24/outline';
import { useGateValue } from '@statsig/react-bindings';
import {
  Component,
  ErrorInfo,
  lazy,
  ReactNode,
  startTransition,
  Suspense,
  useMemo,
} from 'react';
import { useSearchParams } from 'react-router';

import { AppRouteBody } from '@/components/AppRouteBody';
import { TabOption, Tabs } from '@/components/chariot-ui';
import { Loader } from '@/components/Loader';
import { FeatureLock } from '@/components/legacy-ui/FeatureLock';
import { createContactSupportButton } from '@/components/legacy-ui/utils';
import { useFeatureLock } from '@/hooks/useFeatureLock';
import { defenseLevelColors } from '@/sections/attacks/constants';
import { useAuth } from '@/state/auth';

// Re-export colors for external use
// eslint-disable-next-line react-refresh/only-export-components -- Re-exporting constants for convenience
export { defenseLevelColors };

// Lazy load tab components for code splitting
const AttackCoverageTab = lazy(() => import('./tabs/AttackCoverageTab'));
const DefenseCoverageTab = lazy(() => import('./tabs/DefenseCoverageTab'));
const EMB3DCoverageTab = lazy(() => import('./tabs/EMB3DCoverageTab'));
const GapAnalysisTab = lazy(() => import('./tabs/GapAnalysisTab'));
const OverviewTab = lazy(() => import('./tabs/OverviewTab'));
const SessionsTab = lazy(() => import('./tabs/SessionsTab'));

/**
 * Tab configuration for the Validations section.
 * Follows the five-tab architecture with EMB3D for embedded device security.
 */
const allValidationTabs: TabOption[] = [
  { label: 'Overview', value: 'overview' },
  { label: 'MITRE ATT&CK', value: 'attack' },
  { label: 'MITRE D3FEND', value: 'defense' },
  { label: 'MITRE EMB3D', value: 'emb3d' },
  { label: 'Gap Analysis', value: 'gaps' },
  { label: 'Sessions', value: 'sessions' },
];

const DEFAULT_TAB = 'overview';

/**
 * ErrorBoundary component for catching and displaying React errors.
 * Used to wrap Suspense boundaries to provide graceful error handling.
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Validations tab error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

/**
 * Loading skeleton shown while lazy-loaded tabs are being fetched.
 */
function TabLoadingSkeleton() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="text-center">
        <div className="text-chariot-text-secondary mb-2">Loading...</div>
      </div>
    </div>
  );
}

/**
 * Error fallback component shown when a tab fails to load.
 */
function TabErrorFallback() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4">
      <XCircleIcon className="text-chariot-status-critical size-12" />
      <div className="flex flex-col gap-2 text-center">
        <h2 className="text-chariot-text-primary text-lg font-bold">
          Failed to Load
        </h2>
        <p className="text-chariot-text-secondary text-sm">
          There was an error loading this tab. Please refresh the page or try
          again later.
        </p>
      </div>
    </div>
  );
}

/**
 * Validations Section
 *
 * Provides a unified view of security posture through five tabs:
 * - Overview: Executive dashboard with security posture score (Phase 3)
 * - MITRE ATT&CK: MITRE ATT&CK framework visualization (existing functionality)
 * - MITRE D3FEND: MITRE D3FEND defensive countermeasures visualization (Phase 2)
 * - MITRE EMB3D: MITRE EMB3D embedded device security visualization
 * - Gap Analysis: ATT&CK vs D3FEND coverage gaps (Phase 4)
 *
 * Features:
 * - URL-based tab state for shareable links and browser history
 * - Lazy loading with code splitting for each tab
 * - Suspense boundaries for loading states
 * - ErrorBoundary for graceful failure handling
 */
export function Validations() {
  const enableSessions = useGateValue('enable_validations_page');
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');

  const validationTabs = useMemo(
    () =>
      enableSessions
        ? allValidationTabs
        : allValidationTabs.filter(tab => tab.value !== 'sessions'),
    [enableSessions]
  );

  const validTabValues = useMemo(
    () => validationTabs.map(tab => tab.value),
    [validationTabs]
  );

  // Call useFeatureLock for each tab (hooks must be called at top level)
  const overviewLock = useFeatureLock('validations.overview');
  const attackLock = useFeatureLock('validations.attack');
  const defenseLock = useFeatureLock('validations.defense');
  const emb3dLock = useFeatureLock('validations.emb3d');
  const gapsLock = useFeatureLock('validations.gaps');
  const sessionsLock = useFeatureLock('validations.sessions');
  const { isDemoOrg } = useAuth();

  // Check if any tab's feature lock is still loading
  const anyLockLoading =
    overviewLock.isLoading ||
    attackLock.isLoading ||
    defenseLock.isLoading ||
    emb3dLock.isLoading ||
    gapsLock.isLoading ||
    sessionsLock.isLoading;

  // Map tab values to their lock results
  const tabLocks: Record<
    string,
    {
      locked: boolean;
      lockTitle?: string;
      lockDescription?: ReactNode;
      featureName?: string;
    }
  > = {
    overview: overviewLock,
    attack: attackLock,
    defense: defenseLock,
    emb3d: emb3dLock,
    gaps: gapsLock,
    sessions: sessionsLock,
  };

  // Get lock state for a tab
  const getTabLock = (tab: string) => {
    return tabLocks[tab] || { locked: false };
  };

  // Determine initial tab
  const getInitialTab = () => {
    if (validTabValues.includes(tabFromUrl || '')) {
      return tabFromUrl;
    }
    return DEFAULT_TAB;
  };

  const initialTab = getInitialTab();

  // Derive tab directly from URL (single source of truth, no state sync needed)
  const selectedTabValue = useMemo(() => {
    const urlTab = searchParams.get('tab');
    if (urlTab && validTabValues.includes(urlTab)) {
      return urlTab;
    }
    return initialTab || DEFAULT_TAB;
  }, [searchParams, validTabValues, initialTab]);

  // Custom setter that updates URL (which automatically updates selectedTabValue via useMemo)
  const setSelectedTab = (newTab: string) => {
    if (validTabValues.includes(newTab)) {
      // Use startTransition for non-urgent tab content loading
      // Keeps tab button responsive while content loads in background
      startTransition(() => {
        setSearchParams(
          prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('tab', newTab);
            return newParams;
          },
          { replace: true }
        );
      });
    }
  };

  // Find the selected tab object for the Tabs component
  const selectedTab =
    validationTabs.find(tab => tab.value === selectedTabValue) ||
    validationTabs[0];

  // Handle tab selection
  const handleTabSelect = (tab: TabOption) => {
    setSelectedTab(tab.value as string);
  };

  const renderContent = () => {
    // Show loading state while any tab's feature lock is evaluating
    if (anyLockLoading) {
      return (
        <div className="flex h-full items-center justify-center">
          <Loader isLoading type="spinner" />
        </div>
      );
    }

    const { locked, lockTitle, lockDescription, featureName } =
      getTabLock(selectedTabValue);

    const content = (() => {
      switch (selectedTabValue) {
        case 'attack':
          return <AttackCoverageTab />;
        case 'defense':
          return <DefenseCoverageTab />;
        case 'emb3d':
          return <EMB3DCoverageTab />;
        case 'gaps':
          return <GapAnalysisTab />;
        case 'sessions':
          return <SessionsTab />;
        case 'overview':
        default:
          return <OverviewTab />;
      }
    })();

    return (
      <ErrorBoundary fallback={<TabErrorFallback />}>
        <Suspense fallback={<TabLoadingSkeleton />}>
          <FeatureLock
            locked={locked}
            title={lockTitle}
            description={lockDescription}
            action={createContactSupportButton(featureName)}
          >
            {!isDemoOrg && (
              <div className="bg-chariot-background-secondary mb-4 rounded-lg p-4 text-center">
                <p className="text-chariot-text-secondary text-sm font-medium">
                  Situational Awareness Validation Has Not Been Performed
                </p>
              </div>
            )}
            {content}
          </FeatureLock>
        </Suspense>
      </ErrorBoundary>
    );
  };

  return (
    <AppRouteBody title="Validations">
      <div className="flex h-full flex-col overflow-hidden">
        <Tabs
          tabs={validationTabs}
          selected={selectedTab}
          onSelect={handleTabSelect}
          variant="primary"
        />
        <div className="mt-4 h-full overflow-y-auto pb-6">
          {renderContent()}
        </div>
      </div>
    </AppRouteBody>
  );
}
